import type { Session } from "@mux/sdk/v2/client"
import { fetchOracleSession } from "@/transport/oracle"
import type { TransportFetch } from "@/transport/http"
import type { ServerConnection } from "@/context/server"

export type OracleSessionInfo = Session & { isWorkspaceOracle?: boolean }

export function isCanonicalOracleSession(info: unknown, workspaceID: string): info is OracleSessionInfo {
  if (!info || typeof info !== "object") return false
  const session = info as Partial<OracleSessionInfo>
  // The server keeps each Oracle on its workspace's Oracle agent.
  return (
    typeof session.id === "string" &&
    session.id.length > 0 &&
    session.workspaceID === workspaceID &&
    session.hidden === true &&
    session.isWorkspaceOracle === true
  )
}

/** The workspace's live Oracle session among already-synced sessions. */
export function workspaceOracleSession(sessions: readonly Session[], workspaceID: string) {
  return sessions.find((info) => isCanonicalOracleSession(info, workspaceID) && !info.time.archived)
}

/**
 * Oracle lookups and mutations in flight, per machine, so its scope can abort
 * them. The Oracle's record lives only in synced sessions, which the sidebar
 * projection and session events keep current; nothing here stores a copy.
 */
export function createOracleSessionService() {
  const requests = new Map<string, { promise: Promise<OracleSessionInfo>; controller: AbortController }>()
  const mutations = new Map<string, Set<AbortController>>()
  const serverScope = (serverURL: string) => serverURL.replace(/\/+$/, "")
  const requestKey = (serverURL: string, workspaceID: string) => `${serverScope(serverURL)}\n${workspaceID}`

  /** Asks the server for the workspace's Oracle, creating it when the workspace has none; one request per workspace at a time. */
  const load = (input: {
    server: ServerConnection.HttpBase
    workspaceID: string
    fetch?: TransportFetch
    signal?: AbortSignal
  }) => {
    const key = requestKey(input.server.url, input.workspaceID)
    const existing = requests.get(key)
    if (existing) return existing.promise

    const controller = new AbortController()
    const abort = () => controller.abort()
    input.signal?.addEventListener("abort", abort, { once: true })
    if (input.signal?.aborted) controller.abort()
    const request = fetchOracleSession({
      server: input.server,
      workspaceID: input.workspaceID,
      fetch: input.fetch,
      signal: controller.signal,
    }).then((info) => {
      if (!isCanonicalOracleSession(info, input.workspaceID)) throw new Error("server returned a non-canonical Oracle session")
      return info
    }).finally(() => {
      input.signal?.removeEventListener("abort", abort)
      if (requests.get(key)?.promise === request) requests.delete(key)
    })
    requests.set(key, { promise: request, controller })
    return request
  }

  return {
    load,
    async mutate<T>(serverURL: string, operation: (signal: AbortSignal) => Promise<T>): Promise<T> {
      const scope = serverScope(serverURL)
      const controller = new AbortController()
      let active = mutations.get(scope)
      if (!active) mutations.set(scope, active = new Set())
      active.add(controller)
      try {
        return await operation(controller.signal)
      } finally {
        active.delete(controller)
        if (mutations.get(scope) === active && active.size === 0) mutations.delete(scope)
      }
    },
    clearServer(serverURL: string) {
      const scope = serverScope(serverURL)
      for (const controller of mutations.get(scope) ?? []) controller.abort()
      mutations.delete(scope)
      for (const [key, request] of requests) {
        if (!key.startsWith(`${scope}\n`)) continue
        request.controller.abort()
        requests.delete(key)
      }
    },
    sizes: () => ({ requests: requests.size }),
    dispose() {
      for (const request of requests.values()) request.controller.abort()
      for (const active of mutations.values()) for (const controller of active) controller.abort()
      mutations.clear()
      requests.clear()
    },
  }
}

// Utility callers are not all reactive components, so this focused owner is
// explicitly cleared by app server/account lifecycle composition.
const oracleSessions = createOracleSessionService()

export const loadOracleSession = oracleSessions.load
export const clearOracleSessionServer = oracleSessions.clearServer
export const mutateOracleSession = oracleSessions.mutate
